import { HoverImageAction } from '@wix/add-panel-common';
import { CSSProperties, useState } from 'react';
import { useDebugFlags } from '../../../../../../../DebugFlagsProvider';
import { useBoolean } from '../../../../../../../hooks/useBoolean';
import { useGetFallbackImage } from '../../../../../../../hooks/useGetFallbackImage';
import { useUtils } from '../../../../../../../UtilsProvider';
import type { UseItemStyle, UseItemStyleProps } from './useItemStyle.types';

interface HighlightSettings {
  backgroundColor: string;
  borderColor: string;
}

const randomInt = (n: number): number => Math.floor(n * Math.random());

const useContainerHighlightSettings = (): HighlightSettings => {
  const [settings] = useState(() => {
    const color = `${randomInt(255)}, ${randomInt(255)}, ${randomInt(255)}`;

    return {
      backgroundColor: `rgba(${color}, 0.2)`,
      borderColor: `rgba(${color}, 0.5)`,
    };
  });

  return settings;
};

const useHoverImageSrc = (section: UseItemStyleProps['section']): string => {
  const {
    media: { getMediaUrl },
  } = useUtils();
  const getFallbackImage = useGetFallbackImage();

  const {
    additionalBehaviours: { hoverImageAction },
    props: { imageHover, retinaImageHover },
  } = section;

  if (hoverImageAction !== HoverImageAction.Image) {
    return '';
  }

  if (retinaImageHover) {
    const url = getMediaUrl(retinaImageHover, getFallbackImage(retinaImageHover));

    return `--webkit-image-set(url('${url}') 2x)`;
  }

  if (imageHover) {
    const url = getMediaUrl(imageHover, getFallbackImage(imageHover));

    return `url('${url}')`;
  }

  return '';
};

export const useItemStyle = ({ section, item }: UseItemStyleProps): UseItemStyle => {
  const { shouldHighlightPresetContainers } = useDebugFlags();
  const containerHighlightSettings = useContainerHighlightSettings();

  const { value: isHover, setTrue: enableHover, setFalse: disableHover } = useBoolean(false);

  const { preset, liveTextToShow } = item;
  const { rect } = preset;

  const itemStyle: CSSProperties = liveTextToShow
    ? { width: rect.width }
    : { left: rect.x, top: rect.y, width: rect.width, height: rect.height };

  const hoverImageSrc = useHoverImageSrc(section);

  itemStyle.backgroundRepeat = 'no-repeat';
  itemStyle.backgroundPosition = `${-rect.x}px ${-rect.y}px`;
  itemStyle.backgroundImage = hoverImageSrc && isHover ? hoverImageSrc : '';

  if (shouldHighlightPresetContainers) {
    itemStyle.backgroundColor = containerHighlightSettings.backgroundColor;
    itemStyle.border = `1px solid ${containerHighlightSettings.borderColor}`;
  }

  return { itemStyle, enableHover, disableHover };
};
